/* eslint-disable react/jsx-key */
import React, { useEffect } from 'react';
import { ParsedRouteDefinition, MapboxMap } from '../../interfaces';
import { useEventTypes, useFilter } from '../../hooks';
import { hash } from '../../utils';
import { getEnforceStatus } from '../atoms/maps/utils';
import { Enforce } from '../atoms/maps/Enforce';

interface Props {
  routeDefinitions: ParsedRouteDefinition[];
  onClick?: (e: React.MouseEventHandler<HTMLElement>) => void;
  map: MapboxMap;
  showAllEventTypes?: boolean;
}

const lineLayout = {
  'line-cap': 'round',
  'line-join': 'round',
};

const linePaint = {
  'line-color': ['get', 'color'],
  'line-width': 6,
  'line-dasharray': ['get', 'dasharray'],
  'line-opacity': ['get', 'opacity'],
};

const getDasharray = (polygon) => {
  const enforceStatus = getEnforceStatus(polygon.data.enforcement_info?.[0]);

  if (enforceStatus === Enforce.always || enforceStatus === Enforce.never) {
    return [999999, 0.0001];
  }

  return [0.5, 1.5];
};

const getOpacity = (polygon) => {
  const enforceStatus = getEnforceStatus(polygon.data.enforcement_info?.[0]);

  if (enforceStatus === Enforce.never) {
    return 0.4;
  }
  return 1;
};

export function KMLRoutesLayer(props: Props) {
  const { routeDefinitions, map, onClick, showAllEventTypes } = props;
  const filter = useFilter();
  const eventTypes = useEventTypes();
  const selectedEventTypes = filter.selectedFilters;

  useEffect(() => {
    if (!map || !routeDefinitions) return;

    // Remove existing layers on re-render
    routeDefinitions.forEach((_, index) => {
      const layerId = `linesLayer-${index}`;
      const sourceId = `routeSource-${index}`;

      if (map?.getLayer(layerId)) {
        map?.removeLayer(layerId);
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
      }
    });

    // Find the first symbol layer
    const { layers } = map.getStyle();
    const firstSymbolId = layers.find((layer) => layer.type === 'symbol')?.id;

    // Add new layers
    routeDefinitions.forEach((route, routeIndex) => {
      const layerId = `linesLayer-${routeIndex}`;
      const sourceId = `routeSource-${routeIndex}`;

      // Create features collection
      const features = route.polygons
        // only draw selected event types
        .filter((polygon) => {
          // check if polygon type is allowed to the user at all
          if (!eventTypes.some((et) => et.identifier === polygon.type)) {
            return false;
          }

          if (showAllEventTypes || !selectedEventTypes || selectedEventTypes.length === 0) {
            return true;
          }
          return selectedEventTypes.some((et) => et.startsWith(polygon.type));
        })
        .map((polygon, index) => ({
          type: 'Feature' as const,
          properties: {
            color: polygon.color,
            dasharray: getDasharray(polygon),
            opacity: getOpacity(polygon),
            index,
            type: polygon.type,
            routeId: route.id, // Add route ID if available,
            data: polygon.data,
          },
          geometry: {
            type: 'LineString' as const,
            coordinates: polygon.path.map((coord) => [coord.lng, coord.lat]),
          },
        }));

      // Add source
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features,
        },
      });

      // Add layer before the first symbol layer to ensure labels remain on top
      map.addLayer(
        {
          id: layerId,
          type: 'line',
          source: sourceId,
          slot: 'middle',
          layout: lineLayout,
          paint: linePaint,
        },
        firstSymbolId,
      ); // Insert before labels

      // Add click handler if provided
      if (onClick) {
        map.on('click', layerId, onClick);
      }
    });

    // Cleanup function remains the same
    return () => {
      routeDefinitions.forEach((_, index) => {
        const layerId = `linesLayer-${index}`;
        const sourceId = `routeSource-${index}`;

        try {
          if (map?.getLayer(layerId)) {
            if (onClick) {
              map.off('click', layerId, onClick);
            }
            map?.removeLayer(layerId);
          }
          if (map.getSource(sourceId)) {
            map?.removeSource(sourceId);
          }
        } catch (e) {
          console.error(e);
        }
      });
    };
  }, [map, routeDefinitions, onClick, hash(selectedEventTypes), hash(eventTypes), showAllEventTypes]);

  return null;
}
